import type { FeatureSection } from './types'

export const alertsSection: FeatureSection = {
  key: 'alerts',
  title: 'Alerts',
  navPage: 'alerts',
  routes: ['alerts', 'alert-detail'],
  audience: 'everyone',
  image: '/help/screenshots/alerts/_section.png',
  imageAlt: 'The Alerts page: the Status filter, Watch an event and New alert, and the Mine and My colleagues’ tiers.',
  about:
    'An alert watches a job and tells you in your notifications when something you care about happens. It watches one of three things, chosen when you make it: saved tables, a question about the job’s data, or an event such as new data being imported. For tables and questions, the agent states its understanding and waits for your yes, then writes and tests a small check. The check then runs on its schedule with no AI involved. An event alert needs no agent and notifies you each time the event happens. Anyone in the customer can make an event alert and name colleagues to notify. A table or question alert also needs AI access for alerts and for agent sessions.',
  features: [
    {
      key: 'alerts-list',
      name: 'Alerts',
      what: 'The list of alerts in two tiers: Mine, and My colleagues’, which adds an Owner column. Each row shows what it watches (Table, Question or Event), its status, schedule, last result and when it was last checked.',
      how: 'Open Alerts from the Admin menu (under Configuration). Filter by Status if you need to. Click the eye on a row to open the alert. Alerts helper opens the agent in a side pane to find, open and tidy alerts with you.',
      controls: ['Mine', 'My colleagues\'', 'Status', 'All', 'Active', 'Paused', 'Archived', 'View', 'Alerts helper'],
      tools: ['filter_alerts_by_status', 'view_alert'],
      agentNotes:
        'The Alerts helper button opens the agent in a side pane with this page’s tools; it follows agent access (disabled with the reason when you lack it) and starts a fresh chat each time. Deleting an alert from the pane needs the person’s Confirm click. A Manager sees every colleague’s alerts. Anyone else sees colleagues’ alerts except those owned by Managers or by people who are not members of the customer. A person named as a recipient can open the alert from their notification, whoever owns it.',
      image: '/help/screenshots/alerts/alerts-list.png',
      imageAlt: 'The Alerts page header and its Mine and My colleagues’ tiers, with their columns.',
    },
    {
      key: 'new-alert',
      name: 'New alert',
      what: 'Starts a table or question alert. It opens the agent start screen with a New alert card above the message box.',
      how: 'Click New alert. In the card choose Saved tables or A question about the job’s data, give it a Name and pick the Job. Pick one or more tables in the tree, or type the Question. Fill in Tell me when, any context, the schedule and who else to notify. Add anything else in the message box and press Send.',
      controls: ['New alert', 'Saved tables', 'A question about the job\'s data', 'Job', 'Tables', 'Question', 'Tell me when', 'Context (optional)', 'Name'],
      tools: ['create_new_alert'],
      agentNotes:
        'Nothing is saved until Send. Send saves the alert paused with no check, and opens a chat titled “Alert: <name>” on the alert’s job. All tables must be on one job: changing the job clears the chosen tables. New alert needs AI access for alerts and for agent sessions; without it the button is disabled with the reason.',
      image: '/help/screenshots/alerts/new-alert.png',
      imageAlt: 'The New alert card: Saved tables or A question about the job’s data, Name, Job, the table picker, Tell me when, Context, Runs, At and Also notify.',
    },
    {
      key: 'agent-setup',
      name: 'Setting up the check with the agent',
      what: 'In the alert’s chat the agent writes the check, tests it and asks before turning the alert on.',
      how: 'Read the agent’s “My understanding is …” message: the tables, rows and figures it will read, the exact condition, the schedule and who is notified. Answer its questions and say yes. It writes and tests the check, shows a summary and the last test result, then asks whether to turn the alert on.',
      agentNotes:
        'Confirm first: call get_alert, state your understanding, and wait for a yes before writing any check. Do not call create_alert in a setup chat; the alert already exists. If the person changes the tables, condition or schedule in chat, call update_alert and state your understanding again. Save only the exact script that last passed test_alert_script, and call set_alert_status active only after the person says yes. For a question alert, build the crosstab with pt query and fix its axes in the script. The check reads data only.',
      image: '/help/screenshots/alerts/agent-setup.png',
      imageAlt: 'The agent’s “My understanding is” message for a demo alert: the table, the figure, the condition, the message, the schedule and who is notified, ending with a question.',
    },
    {
      key: 'watch-an-event',
      name: 'Watch an event',
      what: 'Makes an event alert: a notice each time something happens on a job. It has no check, no schedule and uses no AI.',
      how: 'Click Watch an event. Give it a name, pick the Job and the Event, and for a social import event optionally a Pipeline. Add anyone else to notify, then click Create alert.',
      controls: ['Watch an event', 'Name', 'Job', 'Event', 'Pipeline', 'Any pipeline', 'Also notify', 'Create alert'],
      tools: ['watch_an_event'],
      settings: [
        'Event: New data imported into the job; A data import failed; A social import batch failed; A social import batch was cut short.',
        'Pipeline: Any pipeline, or one pipeline, for the social import events.',
      ],
      agentNotes: 'An event alert is saved active and notifies on every event, not only on a change. It does not need AI access for alerts.',
      image: '/help/screenshots/alerts/watch-an-event.png',
      imageAlt: 'The Watch an event dialog: Name, Job, Event, Also notify, Cancel and Create alert.',
    },
    {
      key: 'schedule',
      name: 'Runs',
      what: 'When a table or question alert’s check runs.',
      how: 'In the card or the Edit form choose Runs, then the weekday or day of the month where asked, and the time in At.',
      controls: ['Runs', 'Daily', 'Weekly', 'Monthly', 'Weekday', 'Day', 'At'],
      settings: [
        'Runs: Daily, Weekly on a weekday, or Monthly on a day of the month.',
        'At: a time of day, 24-hour. A new alert takes the time zone of the browser that makes it, and the form names that zone. Editing the alert later keeps its zone.',
        'Day: a day past the end of a month runs on that month’s last day.',
      ],
      agentNotes:
        'There is no hourly schedule. An alert that has never run checks within a few minutes of being turned on, whatever its schedule; after that it runs at the scheduled times.',
      image: '/help/screenshots/alerts/schedule.png',
      imageAlt: 'The Runs list open on the New alert card: Daily, Weekly or Monthly, with the time beside it.',
    },
    {
      key: 'recipients',
      name: 'Also notify',
      what: 'Colleagues who get the alert in their notifications as well as the owner.',
      how: 'Type a colleague’s name or email in Also notify and pick them from the list.',
      controls: ['Also notify'],
      agentNotes:
        'Only the owner sets recipients. A Manager may name Managers; others cannot. Recipients see “From <owner>” on the notification. The agent has no tool to set recipients.',
      image: '/help/screenshots/alerts/recipients.png',
      imageAlt: 'The Also notify box on the New alert card, with its note: they get the alert in their notifications too.',
    },
    {
      key: 'notifications',
      name: 'Notifications',
      what: 'How an alert tells you. A table or question alert notifies the owner and recipients when a run finds the condition met and the run before it did not, or when its first run finds it met.',
      how: 'Watch the bell. Open the notification to go to the alert.',
      agentNotes:
        'An alert that stays triggered over many runs notifies once. A check that errors notifies nobody. Test runs never notify; Run now does, like a scheduled run. An event alert notifies on each event.',
    },
    {
      key: 'alert-detail',
      name: 'Alert detail',
      what: 'One alert’s full record: what it watches, the condition and context, who it notifies, the check’s plain-language summary and script, the schedule, and when it was last evaluated and triggered.',
      how: 'Open an alert from the list. Open setup conversation goes back to the chat where the check was written. Back to Alerts returns to the list.',
      controls: ['Back to Alerts', 'Open setup conversation'],
      image: '/help/screenshots/alerts/alert-detail.png',
      imageAlt: 'A demo alert’s page: its name with paused and Table chips, Edit, Run now, Resume and Delete, then the Check with its table, condition, context, recipients and schedule.',
    },
    {
      key: 'set-up-check',
      name: 'Set up check',
      what: 'Asks the agent to write the check for a table or question alert that has none, or whose tables or condition changed.',
      how: 'Click Set up check on the row, or Write the check with the agent on the alert’s page. The agent start screen opens with the alert’s card filled in. Change anything you need and press Send to open the chat.',
      controls: ['Set up check', 'Write the check with the agent'],
      agentNotes: 'Only the owner can set up the check, Managers included, because the agent writes it as the owner. It also needs AI access for agent sessions.',
    },
    {
      key: 'edit',
      name: 'Edit alert',
      what: 'Changes an alert’s name and recipients, a table or question alert’s schedule, and an event alert’s job, event and pipeline. What kind of thing it watches is fixed.',
      how: 'Click Edit on the row or the alert’s page, change the fields and click Save. To change the tables, question, condition or context, click Change with the agent: the agent start screen opens with the alert’s card filled in.',
      controls: ['Edit', 'Edit alert', 'Name', 'Change with the agent', 'Save', 'Cancel'],
      agentNotes:
        'Changing the tables, question or condition clears the check and pauses the alert until a new check is written and tested. Only the owner can change those, the context and the recipients. A Manager who is not the owner can change the name and schedule, pause, resume, run and delete it. To watch a different kind of thing, make a new alert.',
      image: '/help/screenshots/alerts/edit.png',
      imageAlt: 'The Edit alert dialog: the Name, what it watches with Change with the agent, the Runs, Weekday and At fields, Also notify, Cancel and Save.',
    },
    {
      key: 'status',
      name: 'Pause and Resume',
      what: 'An alert is Active, Paused or Archived. Only an Active alert runs.',
      how: 'Click Pause or Resume on the row or the alert’s page.',
      controls: ['Pause', 'Resume'],
      agentNotes:
        'A table or question alert resumes only once it has a saved, tested check; until then Resume is disabled with the reason. An event alert can resume at any time and starts watching from then. The page has no Archive button.',
      image: '/help/screenshots/alerts/status.png',
      imageAlt: 'An alert row marked paused, with the pointer on the Resume icon.',
    },
    {
      key: 'run-now',
      name: 'Run now',
      what: 'Runs a table or question alert’s saved check straight away instead of waiting for its schedule.',
      how: 'Click Run now on the row or the alert’s page. The result appears in History when it finishes.',
      controls: ['Run now'],
      agentNotes: 'Needs a saved check. It works on a paused alert too. Event alerts have no Run now. A run already in progress cannot be started twice. A non-Manager cannot run a colleague’s alert.',
      image: '/help/screenshots/alerts/run-now.png',
      imageAlt: 'An alert row with the pointer on the Run now icon.',
    },
    {
      key: 'history',
      name: 'History',
      what: 'Every run of the alert: when, what started it, the result and any message.',
      how: 'Open the alert and read the History table.',
      settings: ['Trigger: schedule (on its timetable), manual (Run now), test (while the agent set it up) or event.', 'Result: triggered, not triggered, error, or running.'],
      controls: ['Created', 'Trigger', 'Result', 'Detail'],
      image: '/help/screenshots/alerts/history.png',
      imageAlt: 'An alert’s History table with two rows, a manual run and a test, both not triggered.',
    },
    {
      key: 'errors',
      name: 'Errors',
      what: 'A check that fails shows an error on the alert’s page, with the check’s error output, and notifies nobody.',
      how: 'Open the alert to read the error, then ask the agent in the setup conversation to fix the check.',
      agentNotes: 'A check that stops with an error counts as an error even if it signalled a trigger first. A check that runs longer than five minutes is an error.',
    },
    {
      key: 'delete',
      name: 'Delete',
      what: 'Deletes an alert for good.',
      how: 'Click Delete on the row or the alert’s page and type delete to confirm.',
      controls: ['Delete', 'Delete Alert'],
      tools: ['delete_alert'],
      agentNotes: 'Only the owner or a Manager can delete an alert. It cannot be undone; confirm with the person first.',
      image: '/help/screenshots/alerts/delete.png',
      imageAlt: 'The Delete Alert dialog: it names the alert, asks you to type delete to confirm, and offers Cancel and Confirm.',
    },
    {
      key: 'colleagues-alerts',
      name: 'My colleagues\'',
      what: 'Alerts other people in the customer made, shown so you can see what is being watched.',
      how: 'Open the My colleagues’ tier and click View on a row to read the alert.',
      agentNotes: 'On a colleague’s alert, Edit, Pause, Delete and Run now are disabled for anyone but a Manager, with the reason “You can’t change a colleague’s alert.” and the owner to ask.',
    },
  ],
  steps: [
    'Open Alerts and click New alert, or Watch an event for something that happens on a job.',
    'Fill in the card: what to watch, the condition, the schedule and who else to notify, then press Send.',
    'Read the agent’s understanding, answer its questions and say yes.',
    'Check the test result and agree to turn the alert on.',
    'Watch your notifications; open the alert to see its history.',
  ],
  // Shown as pills in this section's page helpers and the Help pane until the first message (Admin → System context replaces them per customer).
  starters: [
    'Which of my alerts are active?',
    'Help me set up a new alert',
    'Explain what one of my alerts checks',
  ],
  intro: 'What would you like an alert to watch for?',
}
